import qs from 'qs';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { Tenant } from 'types/models/admin/Tenant';
import { Contractor } from 'types/models/inventory';
import { PublicUserPropertyDTO } from 'types/models/public/PublicUserProperty';
import { CreateUserRequest } from 'types/requests/admin/CreateUserRequest';
import { UpdateUserRequest } from 'types/requests/admin/UpdateUserRequest';
import { SamplingInstructions, User, Role } from 'types/models/admin';
import { Contract, Private_Contract } from 'types/models/Contract';
import { ROEFormsResponse } from 'types/responses/ROEResponse';
import { TenantForm } from 'types/models';
import { ContractorUserModel } from '@cast/leadcast-types';

export class AdminService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getSamplingInstructions(): Promise<SamplingInstructions[]> {
    const { data } = await this.httpClient.get<SamplingInstructions[]>(`/SamplingInstructions`);
    return data;
  }

  async getUserSelf(): Promise<User> {
    const { data } = await this.usersHttpClient.get<User>(`/User/Self`);
    return data;
  }

  async getClientUsers(): Promise<User[]> {
    const { data } = await this.usersHttpClient.get<User[]>(`/User`);
    return data;
  }

  async getClientRoles(): Promise<Role[]> {
    const { data } = await this.usersHttpClient.get<Role[]>(`/User/ClientRoles`);
    return data;
  }

  async getContractorUsers(): Promise<ContractorUserModel[]> {
    const { data } = await this.publicHttpClient.get<ContractorUserModel[]>(`/contractors/users`, {
      withCredentials: false,
    });
    return data;
  }

  async createContract(contract: Omit<Contract, 'id' | 'contractor'>): Promise<Private_Contract> {
    const { data } = await this.httpClient.post<Private_Contract>(`/Inventory/Contracts`, contract);
    return data;
  }

  //-------------------------------------------------------------------------------
  //The following functions are replacing fetch and axios calls

  async runLeadAssumptionsAlgorithm(
    diameter: string,
    utilityLow: string,
    utilityMedium: string,
    privateLow: string,
    privateMedium: string,
    assumptionType: string
  ): Promise<void> {
    const queryString = qs.stringify({
      diameterCutoff: diameter,
      utilityYearLowLikelihoodLowerBound: utilityLow,
      privateYearLowLikelihoodLowerBound: privateLow,
      utilityYearMediumLikelihoodLowerBound: utilityMedium,
      privateYearMediumLikelihoodLowerBound: privateMedium,
      assumptionType: assumptionType,
    });
    await this.httpClient.post<void>(`/Assumptions?${queryString}`);
  }

  async postTermOptions(newTermOptions: Tenant['termsOptions']): Promise<void> {
    await this.httpClient.post<void>(`/Applications/TermOptions`, newTermOptions);
  }

  async handleAccountUpdate(
    id: string,
    firstName: string,
    lastName: string,
    email: string,
    enabled: boolean,
    emailVerified: boolean
  ): Promise<void> {
    await this.usersHttpClient.post<void>(`/User/UpdateSelf`, {
      id,
      firstName,
      lastName,
      email,
      enabled,
      emailVerified,
    });
  }

  async handlePasswordReset(): Promise<void> {
    await this.usersHttpClient.post<void>(`/User/PasswordResetEmail`);
  }

  async createContractor(payload: Omit<Contractor, 'id'>): Promise<Contractor> {
    const { data } = await this.httpClient.post<Contractor>(`/Inventory/Contractors`, payload);
    return data;
  }

  async updateUser(payload: UpdateUserRequest) {
    await this.usersHttpClient.post<void>(`/User/UpdateUser`, payload);
  }

  async createUser(payload: CreateUserRequest): Promise<User> {
    const { data } = await this.usersHttpClient.post<User>(`/User/CreateUser`, payload);
    return data;
  }

  async deleteUser(userId: User['id']): Promise<User | void> {
    const { data } = await this.usersHttpClient.delete<User | void>(`/User/${userId}`);
    if (data) {
      return data;
    }
  }

  async passwordReset(userId: string): Promise<void> {
    await this.usersHttpClient.post(`/User/PasswordResetEmail/${userId}`);
  }

  async addRole(payload: { userId: User['id']; roles: Role[] }): Promise<User> {
    const { data } = await this.usersHttpClient.post<User>(`/User/UserRoles`, payload);
    return data;
  }

  async deleteRole(payload: { userId: User['id']; roles: Role[] }): Promise<User> {
    const { data } = await this.usersHttpClient.delete<User>(`/User/UserRoles`, payload);
    return data;
  }

  async validatePublicUserProperty(payload: {
    userId: User['id'];
    account: string;
    propertyId: string;
  }): Promise<void> {
    await this.httpClient.post('/Applications/ValidatePublicUserProperty', payload);
  }

  async rejectPublicUserProperty(payload: {
    publicUserPropertyId: PublicUserPropertyDTO['id'];
    rejectionReason: string;
  }): Promise<void> {
    await this.httpClient.post('/Applications/RejectPublicUserProperty', payload);
  }

  async getTenantForms(formType: string): Promise<ROEFormsResponse> {
    const queryString = qs.stringify({
      type: formType,
    });
    const { data } = await this.publicHttpClient.get<ROEFormsResponse>(
      `/tenant/tenantForms?${queryString}`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async saveTenantForm(form: Partial<TenantForm>): Promise<void> {
    await this.publicHttpClient.post(
      `/tenant/saveTenantForm`,
      { form },
      {
        withCredentials: false,
      }
    );
  }

  async publishTenantForm(formId: string): Promise<void> {
    await this.publicHttpClient.put(
      `/tenant/publishTenantForm/${formId}`,
      {},
      {
        withCredentials: false,
      }
    );
  }

  async updateTenantROEManualApproval(enabled: boolean): Promise<number> {
    const { status } = await this.publicHttpClient.patch(
      `/tenant/features/rightOfEntry/manualApproval`,
      { enabled },
      { withCredentials: false }
    );

    return status;
  }
}
